import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import { USAGE_WORK_CATEGORIES } from "../utils/usageInsightsUtils";

const tooltipPaperSx = {
  maxWidth: 380,
  p: 1.25,
};

function UsageTimeSavedEstimateHelpContent() {
  return (
    <Box sx={tooltipPaperSx}>
      <Typography sx={{ fontWeight: 700, fontSize: "0.8125rem", mb: 0.75, color: "inherit" }}>
        How est. time saved is calculated
      </Typography>
      <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.45, mb: 1, color: "inherit" }}>
        Each Theo tool run in the selected period is grouped by category. Estimated time saved is the sum of{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          (actions × minutes per action)
        </Box>{" "}
        for each category. These minutes are typical manual handle-time assumptions, not measured run duration.
      </Typography>
      {USAGE_WORK_CATEGORIES.map((category) => (
        <Box
          key={category.id}
          sx={{
            mb: 0.75,
            pb: 0.75,
            borderBottom: "1px solid",
            borderColor: "rgba(255,255,255,0.12)",
            "&:last-child": { mb: 0, pb: 0, borderBottom: "none" },
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", color: "inherit" }}>
            {category.label} — {category.minutesPerAction} min/action
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", lineHeight: 1.4, color: "inherit", opacity: 0.92 }}>
            {category.description}
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", lineHeight: 1.4, color: "inherit", opacity: 0.85, mt: 0.25 }}>
            Examples: {category.examples.join(", ")}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export interface UsageTimeSavedEstimateHelpProps {
  /** MUI icon font size for the ? control. */
  iconFontSize?: "inherit" | "small" | "medium";
}

/**
 * “?” help for Usage Insights est. time saved — explains category heuristics.
 */
const UsageTimeSavedEstimateHelp: React.FC<UsageTimeSavedEstimateHelpProps> = ({ iconFontSize = "small" }) => (
  <Tooltip
    title={<UsageTimeSavedEstimateHelpContent />}
    arrow
    placement="top"
    enterTouchDelay={0}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#1E293B",
          color: "#F8FAFC",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.24)",
          maxWidth: 400,
        },
      },
      arrow: {
        sx: { color: "#1E293B" },
      },
    }}
  >
    <IconButton
      size="small"
      aria-label="How estimated time saved is calculated"
      sx={{
        p: 0.25,
        color: "#94A3B8",
        "&:hover": { color: "#64748B", bgcolor: "transparent" },
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <HelpOutlineRoundedIcon sx={{ fontSize: iconFontSize === "inherit" ? "inherit" : iconFontSize === "medium" ? 20 : 16 }} />
    </IconButton>
  </Tooltip>
);

export default UsageTimeSavedEstimateHelp;
